import React, { Component } from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import Panel from 'components/Panel/index';
import Button from 'components/Button';
import Preloader from 'components/Preloader';
import NothingBox from 'components/NothingBox';
import ReactSelect from 'components/Select/ReactSelect';
import CircleCheckbox from 'components/CircleCheckbox/CircleCheckbox';
// utils
import { fetchFromAPI } from 'utils/api';
import { showToastrMessage } from 'utils';
import {
  IMPORTED_SENSOR_TYPES, fetchOrganizations, fetchSitesForOrg, toastBarnToolsErrors,
} from './barnToolsConstants';
// styles
import './BarnToolsMapping.scss';

const CONFIDENCE_CLASS = { gateway: 'is-gateway', exact: 'is-exact', fuzzy: 'is-fuzzy', none: 'is-none' };

// Devices still keyed on a gateway number, shown next to the site the matcher
// believes they belong to, so an operator can confirm or correct one at a time.
class BarnToolsMapping extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      savingId: null,
      rows: [],
      sites: [],
      // device id => {siteId, barnId, serials}
      drafts: {},
    };
  }

  componentDidMount() {
    this.fetchAll();
  }

  fetchAll = () => {
    const { companyId } = this.props;
    if (!companyId) return;

    this.setState({ isLoading: true });

    // Sites come one organization at a time - a credential only sees its own -
    // so the company's organizations are merged into a single list of choices.
    Promise.all([
      fetchFromAPI(`/admin/companies/${companyId}/barn_tools_integrations/mapping`),
      fetchOrganizations(fetchFromAPI, companyId).then((organizations) => Promise.all(
        organizations.map((org) => fetchSitesForOrg(fetchFromAPI, companyId, { credentialId: org.id }))
      )),
    ])
      .then(([mapping, siteGroups]) => {
        const rows = mapping.resources || [];

        this.setState({
          isLoading: false,
          rows,
          sites: siteGroups.flat(),
          drafts: rows.reduce((acc, row) => ({
            ...acc,
            [row.id]: {
              siteId: row.suggested_site_id || null,
              barnId: row.suggested_barn_id || null,
              // A device mapped partly starts from what it has, so the operator
              // adds what is missing instead of rebuilding it from the suggestion.
              serials: row.current_serials?.length ? row.current_serials : (row.suggested_serials || []),
            },
          }), {}),
        });
      })
      .catch(() => {
        this.setState({ isLoading: false });
        showToastrMessage('component.toastr.barnTools.error.inventoryFailed', 'error');
      });
  };

  siteById = (siteId) => this.state.sites.find((site) => site.id === siteId) || null;

  barnsFor = (siteId) => this.siteById(siteId)?.barns || [];

  sensorsFor = (siteId, barnId) => {
    const barns = this.barnsFor(siteId);
    const barn = barns.find((b) => b.id === barnId) || (barns.length === 1 ? barns[0] : null);

    return (barn?.sensors || []).filter((sensor) => IMPORTED_SENSOR_TYPES.includes(sensor.typeName));
  };

  updateDraft = (deviceId, patch) => {
    this.setState((prevState) => ({
      drafts: {
        ...prevState.drafts,
        [deviceId]: { ...prevState.drafts[deviceId], ...patch },
      },
    }));
  };

  handleSiteChange = (deviceId, option) => {
    const siteId = option ? option.value : null;
    const barns = this.barnsFor(siteId);

    this.updateDraft(deviceId, {
      siteId,
      barnId: barns.length === 1 ? barns[0].id : null,
      serials: [],
    });
  };

  handleBarnChange = (deviceId, option) => {
    this.updateDraft(deviceId, { barnId: option ? option.value : null, serials: [] });
  };

  toggleSerial = (deviceId, serialNumber) => {
    this.setState((prevState) => {
      const draft = prevState.drafts[deviceId] || {};
      const serials = draft.serials || [];

      return {
        drafts: {
          ...prevState.drafts,
          [deviceId]: {
            ...draft,
            serials: serials.includes(serialNumber)
              ? serials.filter((serial) => serial !== serialNumber)
              : [...serials, serialNumber],
          },
        },
      };
    });
  };

  handleSave = (deviceId) => {
    const { companyId } = this.props;
    const draft = this.state.drafts[deviceId] || {};
    // The site decides which organization - and so which credential - reads
    // the device; without it the backend would keep whichever it had before.
    const orgName = this.siteById(draft.siteId)?.organization?.name;

    this.setState({ savingId: deviceId });

    fetchFromAPI(`/admin/companies/${companyId}/barn_tools_integrations/${deviceId}`, {
      method: 'PUT',
      body: JSON.stringify({
        bt_org_name: orgName,
        bt_site_id: draft.siteId,
        bt_barn_id: draft.barnId,
        sensor_serials: draft.serials,
      }),
    })
      .then(() => {
        showToastrMessage('component.toastr.barnTools.success.mapped');
        this.setState((prevState) => ({
          savingId: null,
          rows: prevState.rows.filter((row) => row.id !== deviceId),
        }));
      })
      .catch((error) => {
        this.setState({ savingId: null });
        toastBarnToolsErrors(error, 'component.toastr.barnTools.error.mappingFailed');
      });
  };

  renderRow = (row) => {
    const { savingId, sites } = this.state;
    const draft = this.state.drafts[row.id] || {};
    const sensors = this.sensorsFor(draft.siteId, draft.barnId);
    const barnOptions = this.barnsFor(draft.siteId).map((barn) => ({ value: barn.id, label: barn.name }));

    const siteOptions = sites.map((site) => ({
      value: site.id,
      label: site.organization?.name ? `${site.name} — ${site.organization.name}` : site.name,
    }));

    return (
      <div className="mapping-row" key={row.id}>
        <div className="device">
          <div className="device-name">{row.farm_name || 'N/A'}</div>
          <div className="device-barn">{row.barn_name || 'N/A'}</div>
          <div className="device-ids">
            <span>{row.gateway_number || 'N/A'}</span>
            <span>{row.phone || ''}</span>
          </div>
          <span className={`confidence ${CONFIDENCE_CLASS[row.confidence] || 'is-none'}`}>
            <FormattedMessage id={`component.barnTools.confidence.${row.confidence}`} />
          </span>
          {row.confidence === 'gateway' && (
            <div className="gateway-note">
              <FormattedMessage id="component.barnTools.locatedByGateway" />
            </div>
          )}
          {row.reason && row.reason !== 'unmapped' && (
            <div className="gap-note">
              <FormattedMessage id={`component.barnTools.gap.${row.reason}`} />
            </div>
          )}
          {Boolean(row.stale_serials?.length) && (
            <div className="gap-detail">
              <FormattedMessage
                id="component.barnTools.gap.staleList"
                values={{ serials: row.stale_serials.join(', ') }}
              />
            </div>
          )}
          {Boolean(row.legacy_sensors?.length) && (
            <div className="gap-detail">
              <FormattedMessage
                id="component.barnTools.gap.legacyList"
                values={{ sensors: row.legacy_sensors.join(', ') }}
              />
            </div>
          )}
          {row.contested && (
            <div className="contested-note">
              <FormattedMessage
                id={row.confidence === 'gateway'
                  ? 'component.barnTools.sharedGateway'
                  : 'component.barnTools.contestedSite'}
              />
            </div>
          )}
        </div>

        <div className="target">
          <div className="form-group">
            <label><FormattedMessage id="component.barnTools.site" /></label>
            <ReactSelect
              options={siteOptions}
              value={draft.siteId ? siteOptions.find((o) => o.value === draft.siteId) : null}
              onChange={(option) => this.handleSiteChange(row.id, option)}
              placeholder={<FormattedMessage id="component.barnTools.selectSite" />}
              noInputMargin
              clearable={false}
              searchable
            />
          </div>

          {barnOptions.length > 1 && (
            <div className="form-group">
              <label><FormattedMessage id="component.barnTools.siteBarn" /></label>
              <ReactSelect
                options={barnOptions}
                value={draft.barnId ? barnOptions.find((o) => o.value === draft.barnId) : null}
                onChange={(option) => this.handleBarnChange(row.id, option)}
                placeholder={<FormattedMessage id="component.barnTools.selectSiteBarn" />}
                noInputMargin
                clearable={false}
                searchable
              />
            </div>
          )}

          {Boolean(sensors.length) && (
            <div className="form-group">
              <label><FormattedMessage id="component.barnTools.sensors" /></label>
              <div className="sensor-picker">
                {sensors.map((sensor) => (
                  <CircleCheckbox
                    key={sensor.serialNumber}
                    className="sensor-option"
                    checked={(draft.serials || []).includes(sensor.serialNumber)}
                    onChange={() => this.toggleSerial(row.id, sensor.serialNumber)}
                    label={sensor.name || sensor.serialNumber}
                    subLabel={`${sensor.typeName} · ${sensor.serialNumber}`}
                  />
                ))}
              </div>
            </div>
          )}

          <Button
            primary
            disabled={!draft.siteId || !(draft.serials || []).length || savingId === row.id}
            onClick={() => this.handleSave(row.id)}
          >
            <FormattedMessage id="component.barnTools.confirmMapping" />
          </Button>
        </div>
      </div>
    );
  };

  render() {
    const { isLoading, rows } = this.state;

    return (
      <div className="BarnToolsMapping">
        <Panel>
          <Panel.Heading title={<FormattedMessage id="component.barnTools.mapping.title" />} />
          <Panel.Body>
            <p className="mapping-intro">
              <FormattedMessage id="component.barnTools.mapping.description" />
            </p>

            {rows.map(this.renderRow)}

            <NothingBox
              itemsName="barn tools integrations"
              display={!rows.length}
              isLoading={isLoading}
            >
              <h2 className="lighter"><FormattedMessage id="component.barnTools.mapping.empty" /></h2>
            </NothingBox>
          </Panel.Body>
        </Panel>

        {isLoading && <Preloader />}
      </div>
    );
  }
}

BarnToolsMapping.propTypes = {
  companyId: T.number,
};

export default BarnToolsMapping;
